import { ControlLabel } from "../ui/Field";
import { useContext, useState } from "react";
import { Notice } from "../ui/Notice";
import { fmtGMilli, gMilliToCoins, parseGCoins } from "../../lib/g-milli";
import { useT, type MsgKey } from "../../lib/i18n";

const BAD: Record<"format" | "decimals" | "zero" | "digits", MsgKey> = {
  format: "econ.g.bad.format", decimals: "econ.g.bad.decimals", zero: "econ.g.bad.zero", digits: "econ.g.bad.digits",
};

/**
 * 「单掷金币 g」一格（model 2 版本的总造价页 / 萌宠星级页）：按金币填（最多 3 位小数），进出都是千分位十进制串（lib/g-milli.ts）。
 *
 * 编辑中留住人敲的原文；解析不了（4 位小数、0、非数字）就标红、不往外交——不悄悄四舍五入成别的数。
 * 清空 = null。失焦显示四舍五入到整金币的千分位分组，聚焦或悬停看精确值。
 * 给了 suggest（按前几关走势外推的推荐值）：空着时灰字显示推荐值，点进去就填上，可以再改。
 */
export function GMilliCell({ value, onChange, disabled, placeholder, label, suggest }: {
  value: string | null | undefined;
  onChange: (milli: string | null) => void;
  disabled?: boolean;
  placeholder?: string;
  label?: string;
  suggest?: string | null;
}) {
  const t = useT();
  const cellLabel = useContext(ControlLabel);
  const [draft, setDraft] = useState<string | null>(null);
  const parsed = draft == null ? null : parseGCoins(draft);
  const bad = parsed != null && !parsed.ok;
  if (suggest && !value) placeholder = t("econ.g.suggest", { v: fmtGMilli(suggest) });
  if (disabled) return <span className="tabular-nums" title={value ? gMilliToCoins(value) : undefined}>{value ? fmtGMilli(value) : <span className="text-ink-placeholder">{placeholder ?? "—"}</span>}</span>;
  return (
    <input
      aria-label={label ?? cellLabel}
      value={draft ?? (value ? fmtGMilli(value) : "")}
      placeholder={placeholder}
      title={parsed && !parsed.ok ? t(BAD[parsed.reason]) : undefined}
      onFocus={() => {
        if (value || !suggest) return setDraft(value ? gMilliToCoins(value) : "");
        setDraft(gMilliToCoins(suggest));
        onChange(suggest);
      }}
      onBlur={() => setDraft(null)}
      onChange={(e) => {
        const raw = e.target.value;
        setDraft(raw);
        const r = parseGCoins(raw);
        if (r.ok && r.milli !== (value ?? null)) onChange(r.milli);
      }}
      className={
        "h-7 w-full rounded-sm border bg-white px-1.5 text-right text-sm tabular-nums text-ink outline-none focus:ring-2 focus:ring-primary-100 " +
        (bad ? "border-danger focus:border-danger" : "border-line focus:border-primary")
      }
    />
  );
}

/** 版本头部的金额口径说明（总造价页、萌宠星级页顶上）：只在单掷金币版本显示怎么填 */
export function EconModelNotice({ model, scale }: {
  model: 1 | 2; scale?: { num: string; den: string } | null; legacyEMilli?: number | null;
}) {
  const t = useT();
  // 只讲当前版本怎么填，不提另一种口径、不露刻度（leo 2026-10-22：页面上看不出口径换过）
  void scale;
  if (model !== 2) return null;
  return <Notice tone="info">{t("econ.ver.model2")}</Notice>;
}
